import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { api } from '../api';
import { useCart } from '../context/CartContext';
import { useToast } from '../components/Toast';

const ShopPage = ({ navigate }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { addToCart } = useCart();
  const toast = useToast();

  useEffect(() => {
    loadItems();
  }, []);

  const loadItems = async () => {
    setError('');
    setLoading(true);
    try {
      const data = await api.getItems();
      const itemsArray = Array.isArray(data) ? data : (data.results || []);
      setItems(itemsArray);
    } catch (err) {
      console.error('Error loading items:', err);
      setError(err.message || 'Unable to load products. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = (item) => {
    try {
      addToCart(item);
      toast.success(`${item.name} added to cart`);
    } catch (err) {
      toast.error('Could not add this item to your cart');
    }
  };

  return (
    <div className="bg-cream min-h-screen">
      <Navbar navigate={navigate} />
      
      {/* Hero Section */}
      <div className="gradient-bg text-white py-14 sm:py-20 pt-28 md:pt-32">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-3xl sm:text-5xl font-bold mb-4 animate-slide-down">
            Travel Essentials Shop
          </h1>
          <p className="text-xl text-cream animate-slide-up">
            Everything you need for your spiritual journey
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-10 sm:py-16">
        {error && (
          <div className="mb-8 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 flex items-center justify-between">
            <span>{error}</span>
            <button onClick={loadItems} className="font-semibold underline">Retry</button>
          </div>
        )}
        {loading ? (
          <div className="text-center py-20">
            <div className="text-6xl mb-4 animate-spin">⏳</div>
            <p className="text-xl text-ink/50">Loading products...</p>
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-20">
            <div className="text-6xl mb-4">📦</div>
            <p className="text-xl text-ink/50">No items available at the moment</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {items.map((item, index) => (
              <div 
                key={item.id} 
                className="lux-card overflow-hidden group"
                style={{animationDelay: `${index * 0.1}s`}}>
                <div className="relative h-64 bg-gradient-to-br from-cream to-sand flex items-center justify-center overflow-hidden">
                  {item.image ? (
                    <img src={item.image} alt={item.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300" />
                  ) : (
                    <span className="text-8xl">📦</span>
                  )}
                  {item.stock_quantity > 0 && item.stock_quantity < 10 && (
                    <div className="absolute top-4 right-4 bg-red-500 text-white px-3 py-1 rounded-full text-sm font-bold">
                      Only {item.stock_quantity} left!
                    </div>
                  )}
                </div>
                
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-2 group-hover:text-primary transition-colors line-clamp-2">
                    {item.name}
                  </h3>
                  <p className="text-ink/65 mb-4 text-sm line-clamp-2">{item.description}</p>
                  
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <p className="text-3xl font-bold text-primary">${item.price}</p>
                      {item.stock_quantity > 0 ? (
                        <p className="text-sm text-primary">✓ In Stock</p>
                      ) : (
                        <p className="text-sm text-red-600">✗ Out of Stock</p>
                      )}
                    </div>
                  </div>
                  
                  <button 
                    onClick={() => handleAddToCart(item)}
                    disabled={item.stock_quantity === 0}
                    className="w-full bg-primary text-white py-3 rounded-lg font-semibold hover:bg-secondary transition-all transform hover:scale-105 disabled:bg-sand disabled:text-ink/40 disabled:cursor-not-allowed disabled:transform-none shadow-lg">
                    {item.stock_quantity === 0 ? 'Out of Stock' : 'Add to Cart 🛒'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <Footer navigate={navigate} />
    </div>
  );
};

export default ShopPage;
